import { injectable, inject } from "inversify";
import { SubscribableOrValue, PureComputed, Subscribable, ObservableArray, Observable, Computed } from "knockout";

import { ManagersContext } from "managers/context";
import { Topic } from "models/topic";
import { TopicCardsScale } from "models/topicCardsScale";
import { TopicCard } from "../../models/topicCard";
import { CardsScoreAnswer } from "models/cardsScoreAnswer";
import { CardsScoreAnswerCard } from "models/cardsScoreAnswerCard";
import { wccModules } from "enums/wccModules";
import { EffectsContainer } from "mixins/withEffects";

import './cardsScoreAnswers.html';
import './cardsScoreAnswers.scss';

export interface CardsScoreAnswersConfig {
    topicId?: SubscribableOrValue<string>
    topic?: SubscribableOrValue<Topic>
    cards?: ObservableArray<TopicCard>
    cardsScales?: ObservableArray<TopicCardsScale>
    answers?: SubscribableOrValue<Array<CardsScoreAnswer>>
    hideTitle?: boolean
    showValues?: boolean
    showScaleNames?: boolean
    showComments: Observable<boolean>
}

export interface CardsScoreAnswersScaleItem {
    id: Subscribable<string>
    title: Subscribable<string>
    scaleLeftName: string
    scaleRightName: string
    values: Array<CardsScoreAnswersCardValue>
    hideTitle: boolean
    showScaleNames: boolean
}

interface CardScoreRealAnswer {
    realValue: string | undefined
    personId: string | undefined
    name: string | undefined
    comment: string | undefined
    aboveAVGValue: boolean
}

export interface CardsScoreAnswersCardValue {
    title: Subscribable<string>
    position: Subscribable<number | undefined>
    realValueStr: Subscribable<string | undefined>
    medianPosition: Subscribable<number | undefined>
    medianStr: Subscribable<string>
    showValues: boolean
    showComments: Observable<boolean>
    realAnswers: CardScoreRealAnswer[] | undefined
}

@injectable()
export default class CardsScoreAnswersVM {
    items: ObservableArray<CardsScoreAnswersScaleItem> = ko.observableArray()

    constructor(
        @inject(wccModules.componentConfig) model: CardsScoreAnswersConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.managersContext) managers: ManagersContext,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        const topicManager = managers.topic(model.topicId);
        const isLoadingTopic = model.topic ? ko.observable(false) : topicManager.pluck('loading', true);
        const topic = (model.topic ?? topicManager.pluck('topic')) as PureComputed<Topic | undefined>
        const scales = model.cardsScales ?? topic.pluck(t => t.cardsScales, []);
        const cards = model.cards ?? topic.pluck(t => t.cards, []);

        const hideTitle = model.hideTitle == true;
        const showValues = model.showValues == true;

        const answers = ko.observable(model.answers).unwrap().default([] as Array<CardsScoreAnswer>);

        const isReady = ko.pureComputed(() => !isLoadingTopic() && answers().length > 0);

        effects.register((scales, cards, answers, isReady) => {
            if (isReady)
                this.items(this.buildItems(scales, cards, answers, hideTitle, showValues, model.showComments, model.showScaleNames == true));
            else
                this.items([]);
        }, [scales, cards, answers, isReady]);

        ko.applyBindingsToNode(info.element, {
            'visible': isReady,

            'css': {
                'cards-score-answers': true
            }
        }, this);
    }

    private buildItems(scales: Array<TopicCardsScale>, cards: Array<TopicCard>, answers: Array<CardsScoreAnswer>, 
                       hideTitle: boolean, showValues: boolean, showComments: Observable<boolean>, showScaleNames: boolean): Array<CardsScoreAnswersScaleItem> {
        const scaleTuples = answers.reduce((result, answer) => {
            const scale = scales.find(scale => scale.id() === answer.cardsScaleId());

            if(scale)
                result.push({ scale, answer });

            return result;
        }, [] as Array<{ scale: TopicCardsScale, answer: CardsScoreAnswer }>);

        const sortedScaleTuples = _(scaleTuples).sortBy(item => item.scale.cardScaleOrder());

        return sortedScaleTuples.reduce((result, { scale, answer }) => {
            const cardTuples = answer.cards().reduce((result, answerCard) => {
                const card = cards.find(card => card.id() === answerCard.cardId());

                if (card)
                    result.push({ card, answerCard });

                return result;
            }, [] as Array<{ card: TopicCard, answerCard: CardsScoreAnswerCard }>);

            const sortedCardTuples = _(cardTuples).sortBy(item => item.card.order());

            result.push({
                id: scale.id as Subscribable<string>,
                title: scale.title,
                scaleLeftName: scale.leftName(),
                scaleRightName: scale.rightName(),
                values: sortedCardTuples.map(item => ({
                    title: item.card.title,
                    position: item.answerCard.value,
                    realValueStr: item.answerCard.realValueStr,
                    medianPosition: item.answerCard.median,
                    medianStr: item.answerCard.medianStr,
                    showValues: showValues,
                    showComments: showComments,
                    realAnswers: item.answerCard.realCards?.map(rc => ({
                        realValue: '(' + rc.RealValue?.toFixed(1) + ')',
                        personId: rc.Person?.PersonId,
                        name: rc.Person?.Name,
                        comment: rc.Comment,
                        aboveAVGValue: +(rc.RealValue ?? 0) > +(item.answerCard.realValue() ?? 0)
                    }))
                })),
                hideTitle: hideTitle,
                showScaleNames: showScaleNames,
            });

            return result;
        }, [] as Array<CardsScoreAnswersScaleItem>);
    }
}